import React, { useEffect, useState } from "react";
import {
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import { auth } from "./firebase";
import rigbyHead from "./rigby-head.png";
import { validatePassword, notifyPasswordChanged, passwordRules } from "./authUtils";

// Handles the links Firebase emails to users (?mode=verifyEmail / ?mode=resetPassword).
export default function AuthActionPage({ mode, oobCode, onDone }) {
  const [status, setStatus] = useState("loading"); // loading | form | success | error
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        if (mode === "verifyEmail") {
          await applyActionCode(auth, oobCode);
          if (!cancelled) setStatus("success");
        } else if (mode === "resetPassword") {
          const addr = await verifyPasswordResetCode(auth, oobCode);
          if (!cancelled) {
            setEmail(addr);
            setStatus("form");
          }
        } else if (!cancelled) {
          setStatus("error");
        }
      } catch (error) {
        console.error("Auth action error:", error?.code);
        if (!cancelled) setStatus("error");
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [mode, oobCode]);

  const submitReset = async (e) => {
    e.preventDefault();
    setFormError("");
    const pwErr = validatePassword(password);
    if (!password) return setFormError("Please enter a new password.");
    if (pwErr) return setFormError(pwErr);
    if (password !== confirm) return setFormError("Passwords do not match.");
    setSaving(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      notifyPasswordChanged(email); // fire and forget
      setStatus("success");
    } catch (error) {
      console.error("Password reset error:", error?.code);
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const isVerify = mode === "verifyEmail";
  let title = "";
  let body = null;

  if (status === "loading") {
    title = isVerify ? "Verifying your email..." : "One moment...";
  } else if (status === "success") {
    title = isVerify ? "Email verified." : "Password updated.";
    body = (
      <>
        <p className="rigby-login-form-subtitle">
          {isVerify
            ? "You're all set. Sign in with your email and password to meet Rigby."
            : "You can now sign in with your new password."}
        </p>
        <button type="button" className="rigby-login-submit-btn" onClick={onDone}>
          Go to sign in
        </button>
      </>
    );
  } else if (status === "form") {
    title = "Choose a new password.";
    body = (
      <form onSubmit={submitReset} noValidate>
        <p className="rigby-login-form-subtitle">For {email}</p>
        <div className="rigby-login-field">
          <label htmlFor="rp-pass">New password</label>
          <div className="rigby-login-password-wrap">
            <input
              id="rp-pass"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="rigby-login-eye-btn"
              onClick={() => setShowPassword((v) => !v)}
              title={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <ul className="rigby-signup-rules" aria-live="polite">
            {passwordRules(password).map((r) => (
              <li key={r.label} className={r.ok ? "ok" : ""}>
                {r.ok ? "\u2713" : "\u2022"} {r.label}
              </li>
            ))}
          </ul>
        </div>
        <div className="rigby-login-field">
          <label htmlFor="rp-confirm">Confirm password</label>
          <input
            id="rp-confirm"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {confirm && confirm !== password && (
            <div className="rigby-signup-error">Passwords do not match</div>
          )}
        </div>
        {formError && <div className="rigby-signup-error" role="alert">{formError}</div>}
        <button type="submit" className="rigby-login-submit-btn" disabled={saving}>
          {saving ? "Saving..." : "Update password"}
        </button>
      </form>
    );
  } else {
    title = "This link didn't work.";
    body = (
      <>
        <p className="rigby-login-form-subtitle">
          It may have expired or already been used. Go back to sign in and request a new one.
        </p>
        <button type="button" className="rigby-login-submit-btn" onClick={onDone}>
          Back to sign in
        </button>
      </>
    );
  }

  return (
    <div className="rigby-login fade-in">
      <div className="rigby-login-brand">
        <div className="rigby-login-brand-bg" />
        <div className="rigby-login-brand-top">
          <img src={rigbyHead} alt="Rigby" className="rigby-login-mark" />
          <span className="rigby-login-wordmark">My Emotional Support Bot</span>
        </div>
        <div className="rigby-login-brand-mid">
          <h1 className="rigby-login-headline">
            Real support for <em>real student life.</em>
          </h1>
          <p className="rigby-login-lede">
            A quiet place to land between classes. Tell Rigby how you're arriving.
          </p>
        </div>
      </div>
      <div className="rigby-login-form-panel">
        <div className="rigby-login-form-card">
          <h2 className="rigby-login-form-title">{title}</h2>
          {body}
        </div>
      </div>
    </div>
  );
}
